/**
 * Zimbra SOAP-over-JSON envelope types.
 *
 * This is the exact wire format Zimbra speaks at `POST /service/soap` when the
 * request is sent as JSON. Verified against the working `mo2mail` prototype
 * (AuthRequest → `authToken[0]._content`) and the live 10.1.16 backend.
 *
 * Command-specific request/response bodies live in the SDK domain modules; this
 * file only models the envelope, context, and fault structures shared by all.
 */
import type { ChangeNotification } from './notify';

/** SOAP namespaces. Each command belongs to exactly one. */
export const JSNS = {
  all: 'urn:zimbra',
  account: 'urn:zimbraAccount',
  mail: 'urn:zimbraMail',
  admin: 'urn:zimbraAdmin',
} as const;

export type Jsns = (typeof JSNS)[keyof typeof JSNS];

/** A command body — shape is command-specific, so we type it as an open object. */
export type JsonObject = Record<string, unknown>;

/** Zimbra wraps many scalar values as `{ _content: <value> }` on the JSON wire. */
export interface Content<T = string> {
  _content: T;
}

/** Account selector used in Auth / delegated requests. */
export interface AccountSelector {
  by: 'name' | 'id' | 'foreignPrincipal' | 'krb5Principal' | 'appAdminName';
  _content: string;
}

/** Session handle echoed by the server; resumed by the client for notifications. */
export interface SessionInfo {
  /** session id */
  id?: string;
  /** legacy: id may arrive as `_content` */
  _content?: string;
  /** session type (e.g. "soap") */
  type?: string;
  /** highest notification sequence the session has produced */
  seq?: number;
}

/** Request-side `<context>` (namespace urn:zimbra). */
export interface SoapRequestContext {
  _jsns: typeof JSNS.all;
  authToken?: string | Content;
  /** create or resume a notification session */
  session?: SessionInfo;
  /** ack: highest notification seq the client has already processed */
  notify?: { seq: number };
  /** run this request against another account (delegated access) */
  account?: AccountSelector;
  /** force JSON responses */
  format?: { type: 'js' | 'xml' };
  csrfToken?: string;
  userAgent?: { name: string; version?: string };
}

/** Response-side `<context>` — carries the piggy-backed change channel. */
export interface SoapResponseContext {
  session?: SessionInfo;
  /** mailbox change token (advances on every committed change) */
  change?: { token: number; type?: string };
  /** notification blocks (the real-time channel) */
  notify?: ChangeNotification[];
  _jsns?: string;
}

export interface SoapRequestEnvelope<TBody extends JsonObject = JsonObject> {
  Header?: { context: SoapRequestContext };
  Body: TBody;
}

export interface SoapResponseEnvelope<TBody extends JsonObject = JsonObject> {
  Header?: { context: SoapResponseContext };
  Body: TBody & { Fault?: SoapFault };
}

/** SOAP 1.2 Fault as Zimbra renders it in JSON. */
export interface SoapFault {
  Code: { Value: string };
  Reason: { Text: string };
  Detail?: {
    Error: {
      /** Zimbra error code, e.g. "service.AUTH_EXPIRED" (confirm exact strings vs live capture) */
      Code: string;
      Trace?: string;
      a?: Array<{ n: string; t?: string; _content: string }>;
    };
  };
}
